import {
  Card,
  CrudMode,
  Grid,
  Stack,
  useElementBreakpoints,
  RichTextEditor,
  Typography,
} from "@compass/core";
import { FilePanel, Select } from "components";
import {
  ApplicationResponseFile,
  FileTypeCategory,
  StudentApplicationEditResource,
  StudentFile,
  StudentServiceFile,
  UploadFile,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import { initDataModel } from "utils";
import { FileDetailTypeEnum } from "./FileDetailTypeEnum";

export type StudentLinkTypeTab = "Existing Student" | "New Student";
const tabs: StudentLinkTypeTab[] = ["Existing Student", "New Student"];

export const FileDetailBody = (props: {
  fileType: FileTypeCategory;
  crudMode: CrudMode;
  studentFile?: StudentFile | undefined;
  studentServiceFile?: StudentServiceFile | undefined;
  applicationResponseFile?: ApplicationResponseFile | undefined;
  applicationFileResource: StudentApplicationEditResource | undefined;
  setEditingApplicationResponseFile?: (value: React.SetStateAction<ApplicationResponseFile | undefined>) => void;
  setEditingStudentServiceFile?: (value: React.SetStateAction<StudentServiceFile | undefined>) => void;
  setEditingStudentFile?: (value: React.SetStateAction<StudentFile | undefined>) => void;
}) => {
  const [uploadFile, setUploadFile] = useState<UploadFile>();
  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();

  useEffect(() => {
    if (!uploadFile) return;

    if (props.fileType === FileTypeCategory.Application && props.applicationResponseFile && props.setEditingApplicationResponseFile) {
      props.setEditingApplicationResponseFile({
        ...props.applicationResponseFile,
        uploadFile: uploadFile
      });
    }
    else if (props.fileType === FileTypeCategory.StudentServiceFile && props.studentServiceFile && props.setEditingStudentServiceFile) {
      props.setEditingStudentServiceFile({
        ...props.studentServiceFile,
        uploadFile: uploadFile
      });
    }
    else if (props.fileType === FileTypeCategory.StudentFile && props.studentFile && props.setEditingStudentFile) {
      props.setEditingStudentFile({
        ...props.studentFile,
        uploadFile: uploadFile
      });
    }
  }, [uploadFile]);

  useEffect(() => {
    const sourceFile = props.fileType === FileTypeCategory.Application ? props.applicationResponseFile
      : props.fileType === FileTypeCategory.StudentServiceFile ? props.studentServiceFile : props.studentFile;

    if (sourceFile?.uploadFile) {
      setUploadFile(sourceFile.uploadFile);
    }
  }, [props.studentFile, props.applicationResponseFile, props.studentServiceFile]);

  return (
    <Stack spacing="lg">
      <Card ref={breakpointsRef}>
        <Stack spacing="xl">
          <Grid container spacing="sm">
            <Grid item xs={6}>
              <FilePanel
                fileName={uploadFile?.fileName}
                fileSize={uploadFile?.fileSize}
                fileRef={uploadFile?.fileRef}
                disabled={uploadFile && uploadFile?.id > 0}
                uploadingData={uploadFile?.uploadingData}
                imagePreviewData={uploadFile?.imagePreviewData}
                onFileChange={(fileName, fileSize, uploadingData) => {
                  let t = initDataModel<UploadFile>({
                    fileName: fileName,
                    fileSize: fileSize,
                    uploadingData: uploadingData,
                  });
                  setUploadFile(t);
                }}
              />
            </Grid>
            <Grid item xs={6}>
              {props.fileType == FileTypeCategory.Application && props.applicationResponseFile && (<Select
                label="Category"
                value={props.applicationFileResource?.fileCategories?.find((x) => x.id === props.applicationResponseFile?.fileCategoryId)}
                options={props.applicationFileResource?.fileCategories}
                getOptionLabel={(option) => option.name}
                onChange={(e, value) => {
                  props.setEditingApplicationResponseFile!(
                    produce(props.applicationResponseFile, (draft) => {
                      draft!.fileCategoryId = value?.id;
                    })
                  )
                }}
              />)}
              {props.fileType == FileTypeCategory.StudentFile && props.studentFile && (<Select
                label="Category"
                value={props.applicationFileResource?.fileCategories?.find((x) => x.id === props.studentFile?.fileCategoryId)}
                options={props.applicationFileResource?.fileCategories}
                getOptionLabel={(option) => option.name}
                onChange={(e, value) => {
                  props.setEditingStudentFile!(
                    produce(props.studentFile, (draft) => {
                      draft!.fileCategoryId = value?.id;
                    })
                  )
                }}
              />)}
              {props.fileType == FileTypeCategory.StudentServiceFile && props.studentServiceFile && (<Select
                label="Category"
                value={props.applicationFileResource?.fileCategories?.find((x) => x.id === props.studentServiceFile?.fileCategoryId)}
                options={props.applicationFileResource?.fileCategories}
                getOptionLabel={(option) => option.name}
                onChange={(e, value) => {
                  props.setEditingStudentServiceFile!(
                    produce(props.studentServiceFile, (draft) => {
                      draft!.fileCategoryId = value?.id;
                    })
                  )
                }}
              />)}
            </Grid>
            <Grid item xs={12}>
              {props.fileType == FileTypeCategory.Application && props.applicationResponseFile && (
                <RichTextEditor
                  label="Note"
                  value={props.applicationResponseFile.note}
                  onChange={(value) =>
                    props.setEditingApplicationResponseFile!(
                      produce(props.applicationResponseFile, (draft) => {
                        draft!.note = value!;
                      })
                    )
                  }
                />
              )}
              {props.fileType == FileTypeCategory.StudentFile && props.studentFile && (
                <RichTextEditor
                  label="Note"
                  value={props.studentFile.note}
                  onChange={(value) =>
                    props.setEditingStudentFile!(
                      produce(props.studentFile, (draft) => {
                        draft!.note = value!;
                      })
                    )
                  }
                />
              )}
              {props.fileType == FileTypeCategory.StudentServiceFile && props.studentServiceFile && (
                <RichTextEditor
                  label="Note"
                  value={props.studentServiceFile.note}
                  onChange={(value) =>
                    props.setEditingStudentServiceFile!(
                      produce(props.studentServiceFile, (draft) => {
                        draft!.note = value!;
                      })
                    )
                  }
                />
              )}
            </Grid>
          </Grid>
        </Stack>
      </Card>
    </Stack>
  );
};
